import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Column, Container, Select } from '@resurs/midas-react'
import { midas } from '@resurs/midas'
import Page from '../../../internal/Page'
import SettingsPanel from '../../../internal/SettingsPanel'
import { getText } from '../../../internal/TextLabels'
import {
  ChannelType,
  ChannelTypesForSelectBox,
} from '../../../models/channelType'
import { PaymentMethodListHeader } from './PaymentMethodListHeader'
import PaymentMethodListItem from './PaymentMethodListItem'
import { updateOrderDataAndFeeRequest } from '../../../api/request-builder/payment-engine-request-builder'
import { useToggle } from '../../../util/utility-hooks'

const insertAndShift = (arr, from, to) => {
  const cutOut = arr.splice(from, 1)[0] // cut the element at index 'from'
  arr.splice(to, 0, cutOut) // insert it at index 'to'
}

const PaymentMethodSettings = ({
  paymentMethods: propsPaymentMethods,
  isFranchiseOrNoRelations,
  ...props
}) => {
  const [edit, toggleEdit] = useToggle(false)
  const [loading, setLoading] = useState(true)
  const [channel, setChannel] = useState(ChannelType.MERCHANT_PORTAL)
  const [paymentMethods, setPaymentMethods] = useState([])
  const [dirty, setDirty] = useState(false)

  const convertPropsPaymentMethods = () =>
    propsPaymentMethods.map(pm => {
      return {
        id: pm.id,
        description: pm.channel.descriptionOverride
          ? pm.channel.descriptionOverride
          : pm.description,
        displayOrder: pm.channel.order,
        paymentMethodValidFrom: pm.validFrom,
        paymentMethodValidTo: pm.validTo,
        validFrom: pm.channel.validFrom,
        validTo: pm.channel.validTo,
        allowRepresentativeToSetFee: pm.allowRepresentativeToSetFee,
        fees: pm.fees,
        fee: pm.fees ? pm.fees[0].amountWithoutVat : null,
      }
    })

  useEffect(() => {
    setLoading(true)
    props
      .getPaymentMethodsByChannel(channel)
      .then(() => setLoading(false))
      .catch(() => setLoading(false))
  }, [channel])

  useEffect(
    () => setPaymentMethods(convertPropsPaymentMethods()),
    [propsPaymentMethods],
  )

  const getListOfUpdatedPaymentMethods = () => {
    return paymentMethods.filter(
      (statePaymentMethod, index) =>
        JSON.stringify(statePaymentMethod) !==
        JSON.stringify(convertPropsPaymentMethods()[index]),
    )
  }

  const changeValue = (id, field, value) => {
    const updated = [...paymentMethods]
    const listOfPaymentMethods = [...paymentMethods]
    const index = listOfPaymentMethods.findIndex(element => element.id === id)
    updated[index][field] = value

    setPaymentMethods(updated)
    setDirty(true)
  }

  const changeOrder = (id, increase) => {
    const listOfPaymentMethods = [...paymentMethods]
    const index = listOfPaymentMethods.findIndex(element => element.id === id)
    const newIndex = increase ? index - 1 : index + 1

    insertAndShift(listOfPaymentMethods, index, newIndex)

    setPaymentMethods(listOfPaymentMethods)
    setDirty(true)
  }

  const renderPaymentMethodComponents = () => (
    <Container>
      {paymentMethods.map((paymentMethod, index) => (
        <PaymentMethodListItem
          key={paymentMethod.id}
          paymentMethod={paymentMethod}
          disableArrowUp={index === 0 || isFranchiseOrNoRelations}
          disableArrowDown={
            index === paymentMethods.length - 1 || isFranchiseOrNoRelations
          }
          edit={edit ? !isFranchiseOrNoRelations : false}
          changeOrder={changeOrder}
          changeValue={changeValue}
        />
      ))}
    </Container>
  )

  const onCancelEdit = () => {
    setPaymentMethods(convertPropsPaymentMethods())
    setDirty(false)
    toggleEdit()
  }

  const onSave = () => {
    props.updateOrderDateAndFee(
      channel,
      updateOrderDataAndFeeRequest(getListOfUpdatedPaymentMethods()),
    )
    setDirty(false)
    toggleEdit()
  }

  const buttons = edit
    ? [
        {
          text: getText('default.cancel'),
          action: onCancelEdit,
        },
        {
          text: getText('default.save'),
          type: 'primary',
          disabled: !dirty,
          action: onSave,
        },
      ]
    : [
        {
          text: getText('default.edit'),
          action: toggleEdit,
        },
      ]

  return (
    <Page>
      {loading ? (
        <div style={{ padding: '2rem' }} className={midas.loading} />
      ) : (
        <SettingsPanel
          header={getText('paymentSettings.paymentMethods.header')}
          buttons={buttons}
        >
          <Column col12 noPadding>
            <Container>
              <div
                style={{
                  paddingTop: '2rem',
                }}
              >
                <label className={midas.formLabel}>
                  {getText('paymentSettings.paymentMethods.selectChannel')}
                </label>
              </div>
            </Container>
          </Column>
          <Column col4 noPadding>
            <Container>
              <div
                style={{
                  paddingBottom: '3rem',
                }}
              >
                <Select
                  disabled={edit}
                  id="channels"
                  value={channel}
                  values={ChannelTypesForSelectBox}
                  onChange={setChannel}
                />
              </div>
            </Container>
          </Column>
          <Column col8 />
          <Column col12>
            <label className={midas.formLabel}>
              {getText('paymentSettings.paymentMethods.paymentMethodsInApp')}
            </label>
          </Column>
          <Column col12 noPadding>
            <PaymentMethodListHeader />
          </Column>
          <Column col12 noPadding>
            {renderPaymentMethodComponents()}
          </Column>
        </SettingsPanel>
      )}
    </Page>
  )
}

PaymentMethodSettings.propTypes = {
  updateOrderDateAndFee: PropTypes.func.isRequired,
  getPaymentMethodsByChannel: PropTypes.func.isRequired,
  paymentMethods: PropTypes.array.isRequired,
  isFranchiseOrNoRelations: PropTypes.bool,
}

PaymentMethodSettings.defaultProps = {
  isFranchiseOrNoRelations: false,
}

export default PaymentMethodSettings
